/**
 * Composable pour la gestion des sessions de formation
 * Limite: 100 lignes max
 */

import { computed } from 'vue'
import { estSessionComplete } from '../../utils/interSessionsUtils.js'

export function useFormationSessions(getFormValue, sessionsData, parseDate) {
  const availableVilles = computed(() => {
    const nom = getFormValue('formation_nom')
    if (!nom) return []

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const villes = [...new Set(
      sessionsData.value
        .filter(s => {
          if (s.Formation !== nom) return false

          // Vérifier qu'il y a au moins une date future
          let dates = s._raw?.datesFormation && Array.isArray(s._raw.datesFormation) ? [...s._raw.datesFormation] : []
          if (dates.length === 0) {
            if (s._raw?.dateDebut) dates.push(s._raw.dateDebut)
            else if (s._raw?.dateFin) dates.push(s._raw.dateFin)
          }

          return dates.some(date => {
            const parsed = parseDate(date)
            if (!parsed) return false
            parsed.setHours(0, 0, 0, 0)
            return parsed >= today
          })
        })
        .map(s => s.Lieu || s.Ville)
        .filter(v => v && v.trim() !== '')
    )]

    return villes.sort()
  })

  const availableSessions = computed(() => {
    const ville = getFormValue('inter_ville')
    const nom = getFormValue('formation_nom')
    if (!ville || !nom) return []
    
    const allOptions = []
    
    sessionsData.value
      .filter(s => {
        // Vérifier que la session correspond au lieu et formation
        if ((s.Lieu || s.Ville) !== ville || s.Formation !== nom) return false
        
        // Vérifier qu'il y a des dates (nouveau format OU ancien format)
        const hasDatesFormation = s._raw?.datesFormation && Array.isArray(s._raw.datesFormation) && s._raw.datesFormation.length > 0
        const hasOldDates = s._raw?.dateDebut || s._raw?.dateFin
        
        return hasDatesFormation || hasOldDates || s['Dates']
      })
      .forEach((s, sessionIndex) => {
        // Parse dates from the raw data
        let datesFormation = s._raw?.datesFormation || []
        let dates = Array.isArray(datesFormation) ? datesFormation : []
        
        // FALLBACK: Si pas de datesFormation, utiliser les anciennes colonnes
        if (dates.length === 0) {
          if (s._raw?.dateDebut && s._raw?.dateFin) {
            // On a date début et fin, créer un tableau avec toutes les dates entre les deux
            const dateDebut = parseDate(s._raw.dateDebut)
            const dateFin = parseDate(s._raw.dateFin)
            if (dateDebut && dateFin) {
              dates = []
              const currentDate = new Date(dateDebut)
              currentDate.setHours(0, 0, 0, 0)
              const endDate = new Date(dateFin)
              endDate.setHours(0, 0, 0, 0)
              
              while (currentDate <= endDate) {
                dates.push(currentDate.toISOString().split('T')[0])
                currentDate.setDate(currentDate.getDate() + 1)
              }
            }
          } else if (s._raw?.dateDebut) {
            dates = [s._raw.dateDebut]
          } else if (s._raw?.dateFin) {
            dates = [s._raw.dateFin]
          }
        }
        
        // Filtrer et trier les dates futures uniquement
        const today = new Date()
        today.setHours(0, 0, 0, 0) // Normaliser à minuit pour comparaison
        
        const futureDates = dates
          .map(date => {
            const dateParsed = parseDate(date)
            if (!dateParsed) return null
            dateParsed.setHours(0, 0, 0, 0)
            return { date, dateParsed }
          })
          .filter(item => item && item.dateParsed >= today)
          .sort((a, b) => a.dateParsed - b.dateParsed)
        
        // Si aucune date future, ignorer cette session
        if (futureDates.length === 0) return
        
        // Formater toutes les dates pour l'affichage : "Le 13/04/2026, 14/04/2026"
        const formattedDates = futureDates.map(({ dateParsed }) => {
          return dateParsed.toLocaleDateString('fr-FR', { 
            day: '2-digit', 
            month: '2-digit', 
            year: 'numeric' 
          })
        }).join(', ')
        
        const disponibilite = s.disponibilite !== null && s.disponibilite !== undefined ? parseInt(s.disponibilite, 10) : null
        const isComplete = estSessionComplete(disponibilite)
        const displayText = isComplete
          ? `Le ${formattedDates} - COMPLET`
          : `Le ${formattedDates}`
        
        // Créer une seule option pour toute la session avec toutes les dates
        allOptions.push({
          id: `${s.Ville}-${s.Formation}-${sessionIndex}`,
          rawId: s._raw?.id || null,
          dates: futureDates.map(({ date }) => date), // Toutes les dates de la session
          dates_display: displayText,
          first_date_parsed: futureDates[0].dateParsed, // Première date pour le tri
          tarif: parseFloat(String(s['Prix de vente (HT)'] || '').replace(/[^\d,.-]/g, '').replace(',', '.')) || 0,
          entreprise: s.Organisme || s.Entreprise,
          disponibilite: disponibilite,
          complete: isComplete,
          companyProfileId: s._raw?.companyProfileId || null,
          typeEntreprise: s._raw?.typeEntreprise || null,
        })
      })
    
    // Trier par date
    return allOptions.sort((a, b) => {
      if (!a.first_date_parsed || !b.first_date_parsed) return 0
      return a.first_date_parsed - b.first_date_parsed
    })
  })

  const selectedSession = computed(() => {
    const sessionId = getFormValue('inter_session_id')
    if (!sessionId) return null
    return availableSessions.value.find(s => s.id === sessionId)
  })

  return {
    availableVilles,
    availableSessions,
    selectedSession
  }
}
